import React, { useEffect, useState } from "react";

// Cycling typewriter for the hero headline.
//
// HYDRATION: "/" is prerendered and then hydrated, so React's first render must
// match the baked HTML exactly. That means the first phrase is rendered WHOLE
// in initial state — never an empty string that types in — and the animation
// only starts in an effect after mount. Get this wrong and the homepage throws
// a hydration mismatch and re-renders the whole tree on the client.
//
// It also means the phrase in the prerendered HTML is the one crawlers and
// social scrapers read, so phrases[0] should be the one that matters.

const TYPE_MS = 55;
const DELETE_MS = 28;
const HOLD_MS = 2200;

const Typewriter = ({ phrases, className = "" }) => {
  const [text, setText] = useState(phrases[0]);

  useEffect(() => {
    // Respect the OS-level motion preference: no typing, just the first phrase.
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || phrases.length < 2) return undefined;

    let cancelled = false;
    let timer;
    let phraseIndex = 0;
    let charCount = phrases[0].length;
    let deleting = true;

    const step = () => {
      if (cancelled) return;
      const current = phrases[phraseIndex];
      if (deleting) {
        charCount -= 1;
        if (charCount <= 0) {
          deleting = false;
          phraseIndex = (phraseIndex + 1) % phrases.length;
          charCount = 0;
        }
      } else {
        charCount += 1;
        if (charCount >= current.length) {
          deleting = true;
          setText(phrases[phraseIndex]);
          timer = setTimeout(step, HOLD_MS);
          return;
        }
      }
      setText(phrases[phraseIndex].slice(0, charCount));
      timer = setTimeout(step, deleting ? DELETE_MS : TYPE_MS);
    };

    timer = setTimeout(step, HOLD_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [phrases]);

  return (
    <span className={`sv-type ${className}`}>
      {text}
      <span className="sv-type__caret" aria-hidden="true" />
    </span>
  );
};

export default Typewriter;
